"use client";

import * as React from "react";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { IconX } from "@tabler/icons-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { ScrollArea } from "./scroll-area";

const sheetSideStyles = {
  bottom:
    "row-start-2 border-t border-border-secondary data-ending-style:translate-y-8 data-starting-style:translate-y-8",
  left: "w-[calc(100%-(--spacing(12)))] max-w-md border-r border-border-secondary data-ending-style:-translate-x-8 data-starting-style:-translate-x-8",
  right:
    "col-start-2 w-[calc(100%-(--spacing(12)))] max-w-md border-l border-border-secondary data-ending-style:translate-x-8 data-starting-style:translate-x-8",
  top: "border-b border-border-secondary data-ending-style:-translate-y-8 data-starting-style:-translate-y-8",
} satisfies Record<SheetSide, string>;

type SheetSide = "right" | "left" | "top" | "bottom";
type SheetVariant = "default" | "inset";

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetBackdrop({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-backdrop"
      className={cn(
        "fixed inset-0 z-modal bg-bg-scrim backdrop-blur-sm transition-opacity duration-200 ease-default data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SheetViewport({
  className,
  side,
  variant = "default",
  ...props
}: SheetPrimitive.Viewport.Props & {
  side?: SheetSide;
  variant?: SheetVariant;
}) {
  return (
    <SheetPrimitive.Viewport
      data-slot="sheet-viewport"
      className={cn(
        "fixed inset-0 z-modal grid",
        side === "bottom" && "grid-rows-[1fr_auto] pt-12",
        side === "top" && "grid-rows-[auto_1fr] pb-12",
        side === "left" && "flex justify-start",
        side === "right" && "flex justify-end",
        variant === "inset" && "sm:p-4",
        className,
      )}
      {...props}
    />
  );
}

function SheetPopup({
  className,
  children,
  side = "right",
  variant = "default",
  showCloseButton = true,
  closeProps,
  portalProps,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  variant?: SheetVariant;
  showCloseButton?: boolean;
  closeProps?: SheetPrimitive.Close.Props;
  portalProps?: SheetPrimitive.Portal.Props;
}) {
  return (
    <SheetPortal {...portalProps}>
      <SheetBackdrop />
      <SheetViewport side={side} variant={variant}>
        <SheetPrimitive.Popup
          data-slot="sheet-popup"
          data-side={side}
          className={cn(
            "relative flex max-h-full min-h-0 w-full min-w-0 flex-col bg-bg-elevated bg-clip-padding text-sm leading-sm text-text-primary shadow-L2 outline-none transition-[opacity,translate] duration-200 ease-in-out will-change-transform data-ending-style:opacity-0 data-starting-style:opacity-0",
            sheetSideStyles[side],
            variant === "inset" &&
              "sm:rounded-xl sm:border sm:border-border-secondary",
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <SheetPrimitive.Close
              aria-label="Close"
              data-slot="sheet-close"
              className="absolute right-3 top-3"
              render={<Button size="icon-2xs" variant="ghost" />}
              {...closeProps}
            >
              <IconX />
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Popup>
      </SheetViewport>
    </SheetPortal>
  );
}

function SheetHeader({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">) {
  const defaultProps = {
    className: cn(
      "flex flex-col gap-0.5 p-4 in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pb-2",
      className,
    ),
    "data-slot": "sheet-header",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

function SheetFooter({
  className,
  variant = "bare",
  render,
  ...props
}: useRender.ComponentProps<"div"> & {
  variant?: "default" | "bare";
}) {
  const defaultProps = {
    className: cn(
      "mt-auto flex flex-col gap-2 p-4",
      variant === "default" &&
        "border-t border-border-secondary bg-tint-tertiary",
      className,
    ),
    "data-slot": "sheet-footer",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "text-md font-medium leading-md text-text-primary",
        className,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm leading-sm text-text-secondary", className)}
      {...props}
    />
  );
}

function SheetPanel({
  className,
  scrollFade: _scrollFade = true,
  render,
  ...props
}: useRender.ComponentProps<"div"> & {
  scrollFade?: boolean;
}) {
  const defaultProps = {
    className: cn("p-4 pt-0", className),
    "data-slot": "sheet-panel",
  };

  return (
    <ScrollArea className="min-h-0 flex-1">
      {useRender({
        defaultTagName: "div",
        props: mergeProps<"div">(defaultProps, props),
        render,
      })}
    </ScrollArea>
  );
}

const SheetContent = SheetPopup;
const SheetOverlay = SheetBackdrop;

export {
  Sheet,
  SheetBackdrop,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPanel,
  SheetPopup,
  SheetPortal,
  SheetPrimitive,
  SheetTitle,
  SheetTrigger,
  SheetViewport,
};
